import { Box, styled, Typography } from "@material-ui/core";
import { Link } from "react-router-dom";
import { linkToEpocusHome } from "../../links";
import {
  EPOCUS_BORDER,
  EPOCUS_PRIMARY,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../epocus-palette";
import { CAPABILITIES, type Capability } from "./capabilities";
import { useEpocusRole } from "./epocus-claim";
import { roleLabel } from "./role-label";

const formatList = (items: readonly string[]): string => {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
};

const allowedRolesSentence = (capability: Capability): string => {
  const labels = CAPABILITIES[capability].map((role) => `${roleLabel(role)}s`);
  return `This area is only available to ${formatList(labels)}.`;
};

const Wrapper = styled(Box)({
  display: "flex",
  justifyContent: "center",
  padding: 24,
});

const Card = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  borderRadius: 12,
  border: `1px solid ${EPOCUS_BORDER}`,
  padding: 24,
  maxWidth: 420,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: 12,
  textAlign: "center",
});

const Title = styled(Typography)({
  fontSize: rem(20),
  fontWeight: 700,
  color: EPOCUS_TEXT_PRIMARY,
});

const Body = styled(Typography)({
  fontSize: rem(14),
  color: EPOCUS_TEXT_SECONDARY,
});

const HomeLink = styled(Link)({
  marginTop: 8,
  fontSize: rem(14),
  fontWeight: 700,
  color: EPOCUS_PRIMARY,
  textDecoration: "none",
});

/**
 * Shared 403 landing surface for route-level RBAC fallbacks — pass it to a
 * `<RoleGate fallback={...}>` at a route boundary. Not for silent in-page
 * control hides. Renders who the area is for (when a `capability` is given),
 * the user's current role, a route home link, and who to contact.
 */
export const AccessDenied: FC<{ capability?: Capability }> = ({
  capability,
}) => {
  const role = useEpocusRole();
  return (
    <Wrapper>
      <Card>
        <Title>Access denied</Title>
        <Body>
          {capability
            ? allowedRolesSentence(capability)
            : "You don't have permission to view this area."}
        </Body>
        {role && <Body>{`You're signed in as a ${roleLabel(role)}.`}</Body>}
        <Body>
          If you think this is a mistake, contact your program administrator.
        </Body>
        <HomeLink to={linkToEpocusHome()}>Back to Home</HomeLink>
      </Card>
    </Wrapper>
  );
};
